import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Collapse, Empty, message, Modal, Spin, Tag, Timeline, Tooltip } from 'antd';
import {
  CarOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  PlusOutlined,
  DeleteOutlined,
  ToolOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import moment from 'moment';

import { DAYTIME_FORMAT } from '../constants';
import { getOrderAuditLog, undoLastOperatorEdit } from '../modules/orders/api';

/**
 * OVO-014 + OVO-020 — Unified order timeline.
 *
 * Merges two log streams into one chronological view so the operator
 * can see, at a glance, everything that ever happened to this order:
 *
 *   - OrderLog rows: driver-real and operator-simulated status
 *     transitions (PICKUP_ARRIVED, COLLECTED, COMPLETED, …). Each row
 *     carries who triggered it (User) and a `simulatedByOperator` flag.
 *     Already on `order.logs` from the backend.
 *
 *   - AuditLog rows: operator-side mutations made from the planner
 *     (OrderProduct CRUD, productDetailsKnown toggle). Loaded lazily
 *     when the section is expanded — keeps the page snappy when the
 *     operator just wants the headline info.
 *
 * Collapsed by default. Designed to sit right before the existing
 * "Advanced" Collapse on the OrderDetail page.
 */

const EVENT_KIND = {
  STATUS_DRIVER: { color: 'green', icon: <CarOutlined /> },
  STATUS_OPERATOR: { color: 'orange', icon: <ToolOutlined /> },
  AUDIT_CREATE: { color: 'blue', icon: <PlusOutlined /> },
  AUDIT_UPDATE: { color: 'cyan', icon: <EditOutlined /> },
  AUDIT_DELETE: { color: 'red', icon: <DeleteOutlined /> },
  UNKNOWN: { color: 'default', icon: <ExclamationCircleOutlined /> },
};

function describeAuditChanges(entry) {
  // Returns a human-readable diff string for the audit log entry.
  if (!entry?.changes) return null;
  if (entry.event === 'CREATE' && entry.changes.after) {
    return `Created ${entry.entityType} #${entry.entityId ?? ''}`.trim();
  }
  if (entry.event === 'DELETE' && entry.changes.before) {
    return `Removed ${entry.entityType} #${entry.entityId ?? ''}`.trim();
  }
  if (entry.event === 'UPDATE') {
    const fields = Object.keys(entry.changes || {});
    if (fields.length === 0) return `${entry.entityType} updated (no field changes)`;
    return fields
      .map((f) => `${f}: ${formatVal(entry.changes[f]?.before)} → ${formatVal(entry.changes[f]?.after)}`)
      .join('  ·  ');
  }
  return entry.event;
}

function formatVal(v) {
  if (v === null || v === undefined || v === '') return '∅';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default function OrderTimeline({ order, defaultOpen = false, canUndo = false, onUndone }) {
  const [auditEntries, setAuditEntries] = useState(null); // null = not yet loaded
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [activeKeys, setActiveKeys] = useState(defaultOpen ? ['timeline'] : []);

  // OVO-208 — undo the last operator data-edit on this order. Confirm first;
  // on success reload the timeline (it gains an UNDO entry) and let the parent
  // refetch the order (reverted products / fields). Driver actions are never
  // touched; the server refuses if the entity changed again since.
  const handleUndo = (e) => {
    e?.stopPropagation?.();
    Modal.confirm({
      title: 'Undo the last operator edit?',
      content:
        'Reverses the most recent operator change to this order (product, field or hint photo) and re-notifies the partner. Driver actions are not affected.',
      okText: 'Undo',
      okButtonProps: { danger: true },
      onOk: async () => {
        const { response, error } = await undoLastOperatorEdit(order.orderCode);
        if (error) {
          message.error(error?.data?.message || error?.message || 'Nothing to undo');
          return;
        }
        message.success(response?.reversal || 'Last operator edit undone');
        setAuditEntries(null); // force the timeline to reload
        onUndone?.();
      },
    });
  };

  // Lazy-load the audit log the first time the section is expanded.
  useEffect(() => {
    if (!activeKeys.includes('timeline') || auditEntries !== null || auditLoading) return;
    setAuditLoading(true);
    setAuditError(null);
    (async () => {
      const { response, error } = await getOrderAuditLog(order.orderCode);
      setAuditLoading(false);
      if (error) {
        setAuditError(error?.data?.message || 'Failed to load audit log');
        setAuditEntries([]);
        return;
      }
      setAuditEntries(Array.isArray(response) ? response : []);
    })();
  }, [activeKeys, order?.orderCode, auditEntries, auditLoading]);

  // Build the merged event list. Status transitions come from
  // `order.logs` (already on the order payload — no extra request).
  // Audit entries come from the lazy-loaded `auditEntries`.
  const events = useMemo(() => {
    const out = [];
    for (const l of order?.logs || []) {
      const kind = l.simulatedByOperator ? EVENT_KIND.STATUS_OPERATOR : EVENT_KIND.STATUS_DRIVER;
      out.push({
        id: `log-${l.id}`,
        createdAt: l.createdAt,
        kind,
        primary: (
          <>
            <Tag color={kind.color} style={{ marginRight: 6 }}>
              {l.status}
            </Tag>
            {l.simulatedByOperator && (
              <Tag color="orange" style={{ marginRight: 6 }}>
                operator-simulated
              </Tag>
            )}
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              by {l.user?.name || l.user?.email || (l.userId ? `user#${l.userId}` : 'system')}
            </span>
          </>
        ),
        details: (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {l.note ? <div>note: {l.note}</div> : null}
            {l.scannedCode ? (
              <div>
                scanned: {l.scannedCode}
                {l.scannedCodeType ? ` (${l.scannedCodeType})` : ''}
              </div>
            ) : null}
            {l.lat != null && l.lng != null ? (
              <div>
                gps: {l.lat.toFixed?.(5) || l.lat}, {l.lng.toFixed?.(5) || l.lng}
              </div>
            ) : null}
          </div>
        ),
      });
    }
    for (const a of auditEntries || []) {
      const kind =
        a.event === 'CREATE'
          ? EVENT_KIND.AUDIT_CREATE
          : a.event === 'DELETE'
            ? EVENT_KIND.AUDIT_DELETE
            : a.event === 'UPDATE'
              ? EVENT_KIND.AUDIT_UPDATE
              : EVENT_KIND.UNKNOWN;
      out.push({
        id: `audit-${a.id}`,
        createdAt: a.createdAt,
        kind,
        primary: (
          <>
            <Tag color={kind.color} style={{ marginRight: 6 }}>
              {a.event} {a.entityType}
            </Tag>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              by {a.user?.name || a.user?.email || `user#${a.userId}`}
            </span>
          </>
        ),
        details: (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            <code style={{ background: 'var(--surface-1)', padding: '1px 4px', borderRadius: 3 }}>
              {describeAuditChanges(a)}
            </code>
            {a.note ? <div style={{ marginTop: 4 }}>note: {a.note}</div> : null}
          </div>
        ),
      });
    }
    // Newest first.
    out.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return out;
  }, [order?.logs, auditEntries]);

  const headerLabel = (
    <span>
      <HistoryOutlined style={{ marginRight: 6 }} />
      Order timeline
      <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
        {(order?.logs?.length || 0) + (Array.isArray(auditEntries) ? auditEntries.length : 0)} events
      </span>
    </span>
  );

  return (
    <Collapse
      activeKey={activeKeys}
      onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
      style={{ marginTop: 8 }}
      items={[
        {
          key: 'timeline',
          label: headerLabel,
          extra: canUndo ? (
            <Button size="small" type="text" danger icon={<UndoOutlined />} onClick={handleUndo}>
              Undo last edit
            </Button>
          ) : undefined,
          children: (
            <div>
              {auditError && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={auditError} />}
              {auditLoading && events.length === 0 ? (
                <Spin />
              ) : events.length === 0 ? (
                <Empty description="No events recorded yet." />
              ) : (
                <Timeline
                  items={events.map((e) => ({
                    key: e.id,
                    color: e.kind.color === 'default' ? 'gray' : e.kind.color,
                    dot: e.kind.icon,
                    children: (
                      <div>
                        <div style={{ marginBottom: 2 }}>{e.primary}</div>
                        {e.details}
                        <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                          <Tooltip title={new Date(e.createdAt).toISOString()}>
                            {moment(e.createdAt).format(DAYTIME_FORMAT)}
                          </Tooltip>
                        </div>
                      </div>
                    ),
                  }))}
                />
              )}
            </div>
          ),
        },
      ]}
    />
  );
}
